import { useAuth } from '../../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { getClients } from '../../services/users';
import { Link, useNavigate } from 'react-router-dom';
import { ContactSupportModal } from '../../components/ui/ContactSupportModal';
import { useContactSupport } from '../../hooks/useContactSupport';

export function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { open, openSupport, closeSupport } = useContactSupport();
  
  const clientsQuery = useQuery({
    queryKey: ['clients-summary'],
    queryFn: () => getClients('', 0, 1),
  });

  const totalClients = clientsQuery.data?.totalElements ?? 0;

  const handleAddClient = () => {
    navigate('/admin/clients');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openAddClientModal'));
    }, 100);
  };

  return (
    <>
      <header className="mb-6 flex flex-col gap-3 sm:mb-8 sm:gap-4 sm:flex-row sm:items-end sm:justify-between lg:mb-12">
        <div className="space-y-2 sm:space-y-3">
          <h1 className="text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
            Good morning, {user?.firstName}.
          </h1>
          <p className="text-sm text-on-surface/70 sm:text-base">
            Here's an update on your practice for {new Date().toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric' 
            })}.
          </p>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <button 
            onClick={handleAddClient}
            className="flex items-center gap-2 rounded-xl bg-secondary px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:px-6 sm:py-3"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            <span className="hidden sm:inline">New Client</span>
            <span className="sm:hidden">Client</span>
          </button>
        </div>
      </header>

      <section className="mb-8 grid grid-cols-1 gap-4 sm:mb-12 sm:grid-cols-2 sm:gap-6 lg:mb-16 lg:grid-cols-2">
        <Link to="/admin/clients" className="group rounded-xl bg-surface-container-low p-8 shadow-editorial transition-all hover:bg-surface-container hover:shadow-ambient">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-white">
              <span className="material-symbols-outlined">people</span>
            </div>
          </div>
          <p className="label-sm mb-2 text-on-surface/60">Total Clients</p>
          <h3 className="text-4xl font-bold text-primary">{totalClients}</h3>
          <p className="mt-2 text-sm text-on-surface/60">Click to view directory</p>
        </Link>

        <button 
          onClick={handleAddClient}
          className="group rounded-xl bg-surface-container-low p-8 shadow-editorial text-left transition-all hover:bg-surface-container hover:shadow-ambient"
        >
          <div className="mb-6 flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-white">
              <span className="material-symbols-outlined">person_add</span>
            </div>
          </div>
          <p className="label-sm mb-2 text-on-surface/60">Quick Action</p>
          <h3 className="text-xl font-bold text-primary">Add New Client</h3>
          <p className="mt-2 text-sm text-on-surface/60">Create a new client account</p>
        </button>
      </section>

      <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-3">
        <section className="space-y-4 sm:space-y-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between sm:mb-6">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">Quick Actions</h2>
          </div>
          
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            <Link to="/admin/clients" className="group rounded-xl bg-surface-container-low p-8 shadow-editorial transition-all hover:bg-surface-container hover:shadow-ambient">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-white">
                <span className="material-symbols-outlined text-2xl">group</span>
              </div>
              <h3 className="mb-2 text-xl font-bold text-primary">Client Directory</h3>
              <p className="text-sm leading-relaxed text-on-surface/70">
                Manage client relationships and track engagement progress.
              </p>
            </Link>

            <button 
              onClick={handleAddClient}
              className="group rounded-xl bg-surface-container-low p-8 shadow-editorial text-left transition-all hover:bg-surface-container hover:shadow-ambient"
            >
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-secondary text-white">
                <span className="material-symbols-outlined text-2xl">person_add</span>
              </div>
              <h3 className="mb-2 text-xl font-bold text-primary">Add New Client</h3>
              <p className="text-sm leading-relaxed text-on-surface/70">
                Create a new client account with email and password.
              </p>
            </button>
          </div>
        </section>

        <section className="space-y-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-3xl font-bold text-primary">Welcome</h2>
          </div>
          
          <div className="rounded-xl bg-surface-container-low p-8 shadow-editorial">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-primary mb-2">Getting Started</h3>
              <p className="text-sm text-on-surface/70 leading-relaxed">
                Welcome to your Mopion admin portal. Start by adding clients and managing their tax cases.
              </p>
            </div>
            <div className="space-y-3">
              <Link 
                to="/admin/clients"
                className="flex items-center gap-3 rounded-lg bg-surface-container-highest px-4 py-3 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container"
              >
                <span className="material-symbols-outlined text-primary">group</span>
                <span>View All Clients</span>
              </Link>
              <button 
                onClick={handleAddClient}
                className="flex w-full items-center gap-3 rounded-lg bg-surface-container-highest px-4 py-3 text-left text-sm font-medium text-on-surface transition-colors hover:bg-surface-container"
              >
                <span className="material-symbols-outlined text-secondary">person_add</span>
                <span>Add New Client</span>
              </button>
            </div>
          </div>

          <div className="gradient-primary relative overflow-hidden rounded-xl p-8 text-white shadow-editorial">
            <div className="relative z-10">
              <p className="label-sm mb-2 opacity-80">Need Assistance?</p>
              <h3 className="mb-2 text-2xl font-bold">Support</h3>
              <p className="text-sm opacity-90 mb-4">Need help with the platform? Our support team is here to assist you.</p>
              <button 
                onClick={openSupport}
                className="inline-flex items-center gap-2 rounded-md bg-secondary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                <span className="material-symbols-outlined text-lg">support_agent</span>
                Contact Support
              </button>
            </div>
          </div>
        </section>
      </div>

      <ContactSupportModal open={open} onClose={closeSupport} />
    </>
  );
}
